'use client';

import { useEffect, useMemo, useRef } from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from 'next-intl';
import { AlertCircle, Bike, Car, CheckCircle2, Info } from 'lucide-react';
import Link from 'next/link';

import { Input } from '@/components/ui/input/input';
import { Label } from '@/components/ui/label/label';
import { PhoneInput } from '@/components/ui/phone-input';
import { useTenantSlug } from '@/hooks/use-tenant-slug';

import { type UseRegistrationAuthResult } from '../../hooks/useRegistrationAuth';
import { identify } from '../../lib/analytics';
import { trackRego } from '../../lib/registration-analytics';
import { useCountries } from '../../hooks/use-public-lookups';
import { useCampaignDefaultMake } from '../../hooks/useCampaignDefaultMake';
import {
  createCompleteRegistrationSchema,
  type CompleteRegistrationFormData,
} from '../../schemas/registration';
import { StepFormActions } from './StepFormActions';
import { VehicleFields } from './VehicleFields';
import {
  REGISTRATION_PHONE_VARIANT,
  REGISTRATION_THEME_CLASSES,
  type RegistrationTheme,
} from './theme';
import { useDeviceCapabilities } from '@/hooks/use-device-capabilities';
import { useScrollIntoViewOnReveal } from '@/hooks/use-scroll-into-view-on-reveal';

interface CompleteRegistrationStepProps {
  onSuccess: () => void;
  onBack?: () => void;
  cityName?: string;
  className?: string;
  /** Event ID for the selected ride */
  eventId: string;
  /** Whether to join the waitlist if the event is at capacity */
  joinWaitlist: boolean;
  /** Auth hook result — passed from parent to share state */
  auth: UseRegistrationAuthResult;
  /** Prefill from an already-authenticated non-entrant session */
  prefilledProfileOverride?: {
    email: string;
    firstName: string;
    lastName: string;
    phone: string | null;
  } | null;
  /** Vehicle type for the entrant profile. Defaults to tenant-slug derivation (dgd → car, else motorcycle). */
  vehicleType?: 'motorcycle' | 'car';
  /**
   * Visual theme. 'dark' (default) keeps the original stone/dgr-gold literals
   * used by the DGR/DGD register pages; 'semantic' uses shadcn tokens so the
   * [data-tenant] CSS scope controls the palette (registration engine).
   */
  theme?: RegistrationTheme;
}

/**
 * CompleteRegistrationStep — shown when an authenticated user has no entrant record.
 * Collects name, phone, and vehicle info to create the entrant profile.
 */
export function CompleteRegistrationStep({
  onSuccess,
  onBack,
  cityName,
  className = '',
  eventId,
  joinWaitlist,
  auth,
  prefilledProfileOverride = null,
  vehicleType: vehicleTypeProp,
  theme = 'dark',
}: CompleteRegistrationStepProps) {
  const c = REGISTRATION_THEME_CLASSES[theme];
  const tAuth = useTranslations('auth');
  const tCommon = useTranslations('common');
  const tRegister = useTranslations('register');
  const authErrorRef = useRef<HTMLDivElement>(null);
  const { isMobile, isKeyboardOpen } = useDeviceCapabilities();

  const { prefilledProfile, completeRegistration, isAuthLoading, authError, clearAuthError } = auth;
  const effectivePrefilledProfile = prefilledProfileOverride ?? prefilledProfile;

  // Translated validation schema
  const completeRegistrationSchema = useMemo(
    () => createCompleteRegistrationSchema(tAuth),
    [tAuth],
  );

  const { data: countries = [] } = useCountries();

  const tenantSlug = useTenantSlug();
  const isDgd = tenantSlug === 'dgd';
  const vehicleType: 'motorcycle' | 'car' = vehicleTypeProp ?? (isDgd ? 'car' : 'motorcycle');
  const isCar = vehicleType === 'car';
  const vehicleLabel = isCar ? tAuth('yourVehicle') : tAuth('yourRide');
  const VehicleIcon = isCar ? Car : Bike;

  const form = useForm<CompleteRegistrationFormData>({
    resolver: zodResolver(completeRegistrationSchema),
    defaultValues: {
      firstName: effectivePrefilledProfile?.firstName ?? '',
      lastName: effectivePrefilledProfile?.lastName ?? '',
      phone: effectivePrefilledProfile?.phone ?? '',
      vehicleYear: '',
      vehicleMake: '',
      vehicleModel: '',
    },
  });

  // DGD vehicle eligibility — cars must be 40+ years old.
  const watchedVehicleYear = useWatch({ control: form.control, name: 'vehicleYear' });
  const showEligibilityWarning = useMemo(() => {
    if (!isDgd) return false;
    const yearNum = Number(watchedVehicleYear);
    if (!Number.isFinite(yearNum) || yearNum <= 0) return false;
    const currentYear = new Date().getFullYear();
    return currentYear - yearNum < 40;
  }, [isDgd, watchedVehicleYear]);

  // Reset form defaults when prefilledProfile changes. Preserve any vehicle
  // entries the rider has made (or the seeded default make) across the reset.
  useEffect(() => {
    if (effectivePrefilledProfile) {
      form.reset({
        firstName: effectivePrefilledProfile.firstName,
        lastName: effectivePrefilledProfile.lastName,
        phone: effectivePrefilledProfile.phone ?? '',
        vehicleYear: form.getValues('vehicleYear'),
        vehicleMake: form.getValues('vehicleMake'),
        vehicleModel: form.getValues('vehicleModel'),
      });
    }
  }, [effectivePrefilledProfile, form]);

  // Pre-select the campaign's default vehicle make once, without clobbering a
  // make the rider has already chosen.
  const defaultVehicleMake = useCampaignDefaultMake(eventId);
  const seededMakeRef = useRef(false);
  useEffect(() => {
    if (seededMakeRef.current || !defaultVehicleMake) return;
    if (!form.getValues('vehicleMake')) {
      form.setValue('vehicleMake', defaultVehicleMake);
    }
    seededMakeRef.current = true;
  }, [defaultVehicleMake, form]);

  useScrollIntoViewOnReveal(authErrorRef, !!authError, {
    enabled: isMobile,
    block: 'start',
    focus: true,
  });

  async function handleSubmit(data: CompleteRegistrationFormData) {
    clearAuthError();
    const result = await completeRegistration({
      ...data,
      defaultBlog: {
        title: tRegister('defaultBlogTitle'),
        content: tRegister('defaultBlogContent'),
      },
      eventId,
      joinWaitlist,
    });
    if (result.success) {
      if (auth.entrant?.userId) identify(auth.entrant.userId);
      trackRego('rego_auth_completed', { auth_mode: 'complete-registration' });
      onSuccess();
    }
  }

  return (
    <div className={className}>
      <h1 className={`text-3xl md:text-4xl font-bold ${c.heading} mb-2`}>
        {tAuth('completeYourRegistration')}
      </h1>
      {cityName && (
        <p className={`${c.subtitle} mb-8`}>
          {tAuth.rich('registeringFor', {
            cityName,
            highlight: (chunks: React.ReactNode) => (
              <span className={`${c.highlight} font-medium`}>{chunks}</span>
            ),
          })}
        </p>
      )}

      {/* Info Banner */}
      <div className={c.infoBanner}>
        <Info className={c.infoBannerIcon} aria-hidden="true" />
        <p className={c.infoBannerText}>
          {tAuth('completeRegistrationNotice', { email: effectivePrefilledProfile?.email ?? '' })}
        </p>
      </div>

      {/* Error Alert */}
      {authError && (
        <div
          ref={authErrorRef}
          tabIndex={-1}
          role="alert"
          aria-live="assertive"
          className={c.errorBox}
        >
          <AlertCircle className={c.errorIcon} aria-hidden="true" />
          <div>
            <p className={c.errorTitle}>{tAuth('signupFailed')}</p>
            <p className={c.errorBody}>{authError}</p>
          </div>
        </div>
      )}

      <form
        id="registration-auth-complete-form"
        onSubmit={form.handleSubmit(handleSubmit)}
        className="space-y-5 pb-28 md:pb-0"
        autoComplete="on"
      >
        {/* Email (display only) */}
        <div className="space-y-2">
          <Label htmlFor="complete-email" className={c.label}>
            {tAuth('emailAddress')}
          </Label>
          <Input
            id="complete-email"
            type="email"
            value={effectivePrefilledProfile?.email ?? ''}
            disabled
            className={c.disabledInput}
          />
        </div>

        {/* Name */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="complete-firstName" className={c.label}>
              {tAuth('firstName')}
            </Label>
            <Input
              id="complete-firstName"
              {...form.register('firstName')}
              className={c.input}
              placeholder={tAuth('firstNamePlaceholder')}
              autoComplete="given-name"
              aria-describedby={
                form.formState.errors.firstName ? 'complete-firstName-error' : undefined
              }
              aria-invalid={!!form.formState.errors.firstName}
            />
            {form.formState.errors.firstName && (
              <p id="complete-firstName-error" role="alert" className={c.fieldError}>
                {form.formState.errors.firstName.message}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="complete-lastName" className={c.label}>
              {tAuth('lastName')}
            </Label>
            <Input
              id="complete-lastName"
              {...form.register('lastName')}
              className={c.input}
              placeholder={tAuth('lastNamePlaceholder')}
              autoComplete="family-name"
              aria-describedby={
                form.formState.errors.lastName ? 'complete-lastName-error' : undefined
              }
              aria-invalid={!!form.formState.errors.lastName}
            />
            {form.formState.errors.lastName && (
              <p id="complete-lastName-error" role="alert" className={c.fieldError}>
                {form.formState.errors.lastName.message}
              </p>
            )}
          </div>
        </div>

        {/* Phone */}
        <div className="space-y-2">
          <Label htmlFor="complete-phone" className={c.label}>
            {tAuth('phone')} <span className={c.requiredMark}>*</span>
          </Label>
          <Controller
            name="phone"
            control={form.control}
            render={({ field }) => (
              <PhoneInput
                id="complete-phone"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                defaultCountry="AU"
                countries={countries}
                variant={REGISTRATION_PHONE_VARIANT[theme]}
                size="lg"
              />
            )}
          />
          {form.formState.errors.phone && (
            <p id="complete-phone-error" role="alert" className={c.fieldError}>
              {form.formState.errors.phone.message}
            </p>
          )}
        </div>

        {/* Vehicle Section Divider */}
        <div className="flex items-center gap-3 pt-4">
          <div className={c.dividerLine} />
          <span className={c.dividerLabel}>
            <VehicleIcon className="w-4 h-4" />
            {vehicleLabel}
          </span>
          <div className={c.dividerLine} />
        </div>

        {/* Year, Make, Model - 3 columns */}
        <VehicleFields
          control={form.control}
          theme={theme}
          vehicleType={vehicleType}
          idPrefix="complete"
        />

        {/* DGD vehicle eligibility callout — slides out when year < 40 yrs old */}
        <div
          className={`grid transition-all duration-300 ease-out ${
            showEligibilityWarning ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
          }`}
          aria-hidden={!showEligibilityWarning}
        >
          <div className="overflow-hidden">
            <div className="mt-2 flex gap-3 rounded-lg border border-dgd-gold/40 bg-dgd-cream/95 p-4 text-dgd-charcoal">
              <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-dgd-tan" />
              <div className="space-y-1.5">
                <p className="font-semibold text-sm">{tAuth('eligibilityCheckTitle')}</p>
                <p className="text-sm leading-relaxed">
                  {tAuth('eligibilityCheckBodyDgd')}{' '}
                  <Link
                    href="/about/vehicle-eligibility"
                    className="font-semibold underline underline-offset-2 hover:text-dgd-tan"
                  >
                    {tAuth('eligibilityCheckLink')}
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <StepFormActions
          stepNumber={2}
          stepLabel={tRegister('yourDetails')}
          submitFormId="registration-auth-complete-form"
          submitLabel={tAuth('completeRegistrationButton')}
          loadingLabel={tAuth('completingRegistration')}
          isLoading={isAuthLoading}
          showBack={!!onBack}
          onBack={onBack}
          backLabel={tCommon('back')}
          isKeyboardOpen={isKeyboardOpen}
          theme={theme}
        />
      </form>
    </div>
  );
}
